import React, { useState } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CampLocation } from "@/types/campLocation";
import { aiSessionService, AISessionFetchRequest, SessionPreviewData } from "@/services/aiSessionService";
import { AISessionFetchPreview } from "./AISessionFetchPreview";
import { toast } from "@/components/ui/sonner";

// Form schema
const aiSessionFetchSchema = z.object({
  action: z.enum(["update", "create"], {
    required_error: "Please select what you want to do"
  }),
  model: z.string().min(1, {
    message: "Please select a model"
  }),
  fields: z.array(z.enum(["title", "url", "price", "availability", "description"])).min(1, {
    message: "Please select at least one field"
  }),
  crawlDepth: z.number().min(0).max(1, {
    message: "Crawl depth must be 0 or 1"
  }),
  pageCrawlMax: z.number().min(1).max(30, {
    message: "Page crawl max must be between 1 and 30"
  }),
  programKeys: z.array(z.string()).min(1, {
    message: "Please select at least one program"
  }),
  sourceUrl: z.string().optional(),
  sourceContent: z.string().optional(),
  sourceFile: z.any().optional(),
  instructionalNotes: z.string().optional()
}).refine(
  (data) => data.sourceUrl || data.sourceContent || data.sourceFile,
  {
    message: "Please provide at least one source (URL, content, or file)",
    path: ["sourceUrl"] // This will show the error on the sourceUrl field
  }
);

type AISessionFetchFormData = z.infer<typeof aiSessionFetchSchema>;

interface AISessionFetchModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: CampLocation;
  locationId: string;
}

const FIELD_OPTIONS = [
  { id: "title", label: "Title" },
  { id: "url", label: "URL" },
  { id: "price", label: "Price" },
  { id: "availability", label: "Availability" },
  { id: "description", label: "Description" }
] as const;

const MODEL_OPTIONS = [
  { id: "gemini-1.5-pro", label: "Gemini 1.5 Pro (Recommended)" },
  { id: "gemini-1.5-flash", label: "Gemini 1.5 Flash (Faster)" }
] as const;

export const AISessionFetchModal: React.FC<AISessionFetchModalProps> = ({
  isOpen,
  onClose,
  location,
  locationId
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState<SessionPreviewData[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const form = useForm<AISessionFetchFormData>({
    resolver: zodResolver(aiSessionFetchSchema),
    defaultValues: {
      action: "update",
      model: "gemini-1.5-pro",
      fields: [],
      crawlDepth: 0,
      pageCrawlMax: 5,
      programKeys: [],
      sourceUrl: "",
      sourceContent: "",
      instructionalNotes: ""
    }
  });

  // Watch form for changes to set dirty state
  const watchedValues = form.watch();
  React.useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (type === 'change') {
        setIsDirty(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: AISessionFetchFormData) => {
    console.log("[AISessionFetchModal] Preview fetch requested:", data);
    setIsLoading(true);

    try {
      const request: AISessionFetchRequest = {
        locationId,
        action: data.action,
        model: data.model,
        fields: data.fields,
        crawlDepth: data.crawlDepth,
        pageCrawlMax: data.pageCrawlMax,
        programKeys: data.programKeys,
        sourceUrl: data.sourceUrl || undefined,
        sourceContent: data.sourceContent || undefined,
        sourceFile: data.sourceFile || undefined,
        instructionalNotes: data.instructionalNotes || undefined
      };

      const response = await aiSessionService.generatePreview(request);

      if (response.success) {
        setPreviewData(response.previewData);
        setShowPreview(true);
        toast.success(`Generated preview for ${response.previewData.length} sessions`);
      } else {
        toast.error(`Failed to generate preview: ${response.error}`, {
          duration: Infinity, // Keep visible until manually closed
          dismissible: true,   // Show X button to close
          action: {
            label: "Close",
            onClick: () => {},
          }
        });
      }
    } catch (error) {
      console.error("[AISessionFetchModal] Error generating preview:", error);
      toast.error("Failed to generate preview. Please try again.", {
        duration: Infinity, // Keep visible until manually closed
        dismissible: true,   // Show X button to close
        action: {
          label: "Close", 
          onClick: () => {},
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    console.log("[AISessionFetchModal] Modal closing");
    setIsDirty(false);
    setShowPreview(false);
    setPreviewData([]);
    form.reset();
    onClose();
  };

  const handlePreviewClose = () => {
    setShowPreview(false);
  };

  const handleBack = () => {
    setShowPreview(false);
  };

  const handleApplyChanges = async (data: SessionPreviewData[]) => {
    console.log("[AISessionFetchModal] Applying changes:", data);
    setIsLoading(true);

    try {
      const response = await aiSessionService.applyChanges({
        previewData: data,
        action: form.getValues("action"),
        locationId
      });

      if (response.success) {
        toast.success(`Successfully ${form.getValues("action") === "update" ? "updated" : "created"} ${response.updatedCount || response.createdCount || 0} sessions`);
        const rejected = response.rejectedStatusValues ?? 0;
        if (rejected > 0) {
          toast.warning(
            form.getValues("action") === "update"
              ? `${rejected} registration/availability status value(s) from the AI were not valid (or conflicted) and were NOT written. Check those sessions' statuses.`
              : `${rejected} registration/availability status value(s) from the AI were not valid; those sessions were created with an Unknown status.`,
            { duration: Infinity, dismissible: true },
          );
        }
        handleClose();
      } else {
        toast.error(`Failed to apply changes: ${response.error}`, {
          duration: Infinity, // Keep visible until manually closed
          dismissible: true,   // Show X button to close
          action: {
            label: "Close",
            onClick: () => {},
          }
        });
      }
    } catch (error) {
      console.error("[AISessionFetchModal] Error applying changes:", error);
      toast.error("Failed to apply changes. Please try again.", {
        duration: Infinity, // Keep visible until manually closed
        dismissible: true,   // Show X button to close
        action: {
          label: "Close",
          onClick: () => {},
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get available programs from location variations
  const availablePrograms = location.variations?.map(variation => ({
    key: variation.key,
    name: variation.name || variation.key
  })) || [];

  return (
    <>
      <BigFormModal
        isOpen={isOpen && !showPreview}
        onClose={handleClose}
        onSave={form.handleSubmit(onSubmit)}
        title="AI-Powered Session Data Fetch"
        saveLabel="Preview the Fetch"
        isDirty={isDirty}
        isSaving={isLoading}
        description="Use AI to extract and process camp session data from various sources"
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            
            {/* What do you want to do? */}
            <FormField
              control={form.control}
              name="action"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What do you want to do?</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select action" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="update">Update sessions</SelectItem>
                      <SelectItem value="create">Create sessions</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* AI Model Selection */}
            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AI Model</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select AI model" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MODEL_OPTIONS.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Choose the AI model for processing your request
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Fields */}
            <FormField
              control={form.control}
              name="fields"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fields</FormLabel>
                  <div className="grid grid-cols-2 gap-4">
                    {FIELD_OPTIONS.map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`field-${option.id}`}
                          checked={field.value?.includes(option.id as any)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...field.value, option.id]);
                            } else {
                              field.onChange(field.value?.filter((value) => value !== option.id));
                            }
                          }}
                        />
                        <label
                          htmlFor={`field-${option.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Crawl depth */}
            <FormField
              control={form.control}
              name="crawlDepth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Crawl depth</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="1"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <p className="text-sm text-muted-foreground">
                    Crawl depth (0 = no crawling, 1 = crawl all pages)
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Page crawl max */}
            <FormField
              control={form.control}
              name="pageCrawlMax"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page crawl max</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      placeholder="5"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 5)}
                    />
                  </FormControl>
                  <p className="text-sm text-muted-foreground">
                    Maximum number of pages to crawl (1-30, defaults to 5)
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Limit to these programs */}
            <FormField
              control={form.control}
              name="programKeys"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Limit to these programs</FormLabel>
                  <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                    {availablePrograms.map((program) => (
                      <div key={program.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`program-${program.key}`}
                          checked={field.value?.includes(program.key)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...field.value, program.key]);
                            } else {
                              field.onChange(field.value?.filter((value) => value !== program.key));
                            }
                          }}
                        />
                        <label
                          htmlFor={`program-${program.key}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {program.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Source URL */}
            <FormField
              control={form.control}
              name="sourceUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source URL (optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com/camp-schedule"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Source Content */}
            <FormField
              control={form.control}
              name="sourceContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Content (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Paste HTML, text, or other content here..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Source File */}
            <FormField
              control={form.control}
              name="sourceFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source File (optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="file"
                      accept=".pdf,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file);
                      }}
                    />
                  </FormControl>
                  <p className="text-sm text-muted-foreground">
                    Accepted formats: PDF, TXT
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Additional instructional notes */}
            <FormField
              control={form.control}
              name="instructionalNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional instructional notes (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Provide additional context or instructions for the AI..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

          </form>
        </Form>
      </BigFormModal>

      {/* Preview Modal */}
      <AISessionFetchPreview
        isOpen={showPreview}
        onClose={handlePreviewClose}
        onBack={handleBack}
        previewData={previewData}
        selectedFields={form.getValues("fields")}
        action={form.getValues("action")}
        onApplyChanges={handleApplyChanges}
      />
    </>
  );
}; 